/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Semantic Surfaces Hook
 *
 * Memoizes common design token surface and interaction colors to reduce
 * repeated useDesignTokens calls and prevent unnecessary rerenders.
 */

import type { } from "react";

import type {
  SemanticIntent,
  SemanticInteractionColors,
  SemanticSurfaceColors,
  SurfaceContext,
} from "./use-design-tokens";
import { useDesignTokens } from "./use-design-tokens";

export interface SemanticSurfaces {
  /** Neutral surface colors */
  neutral: SemanticSurfaceColors;
  /** Neutral interaction colors */
  neutralInteraction: SemanticInteractionColors;
  /** Primary surface colors */
  primary: SemanticSurfaceColors;
  /** Primary interaction colors */
  primaryInteraction: SemanticInteractionColors;
  /** Success surface colors */
  success: SemanticSurfaceColors;
  /** Success interaction colors */
  successInteraction: SemanticInteractionColors;
  /** Warning surface colors */
  warning: SemanticSurfaceColors;
  /** Warning interaction colors */
  warningInteraction: SemanticInteractionColors;
  /** Error surface colors */
  error: SemanticSurfaceColors;
  /** Error interaction colors */
  errorInteraction: SemanticInteractionColors;
  /** Info surface colors */
  info: SemanticSurfaceColors;
  /** Info interaction colors */
  infoInteraction: SemanticInteractionColors;
  /** Helper to get surface colors for any intent */
  getSurface: (intent: SemanticIntent) => SemanticSurfaceColors;
  /** Helper to get interaction colors for any intent */
  getInteraction: (intent: SemanticIntent) => SemanticInteractionColors;
}

/**
 * Memoizes common semantic surface and interaction colors.
 * Reduces repeated useDesignTokens calls across components.
 *
 * @example
 * ```tsx
 * const surfaces = useSemanticSurfaces();
 *
 * // Use pre-memoized surfaces
 * <div style={{ background: surfaces.primary.background }}>
 *   {content}
 * </div>
 *
 * // Or get dynamic surfaces
 * const customSurface = surfaces.getSurface('warning');
 * ```
 */
export function useSemanticSurfaces(
  context: SurfaceContext = "on-light",
): SemanticSurfaces {
  const { getSemanticSurfaceColors, getSemanticInteractionColors } =
    useDesignTokens();

  const neutral = getSemanticSurfaceColors("neutral", context);
  const neutralInteraction = getSemanticInteractionColors("neutral");
  const primary = getSemanticSurfaceColors("primary", context);
  const primaryInteraction = getSemanticInteractionColors("primary");
  const success = getSemanticSurfaceColors("success", context);
  const successInteraction = getSemanticInteractionColors("success");
  const warning = getSemanticSurfaceColors("warning", context);
  const warningInteraction = getSemanticInteractionColors("warning");
  const error = getSemanticSurfaceColors("error", context);
  const errorInteraction = getSemanticInteractionColors("error");
  const info = getSemanticSurfaceColors("info", context);
  const infoInteraction = getSemanticInteractionColors("info");

  const getSurface = (intent: SemanticIntent): SemanticSurfaceColors => {
    switch (intent) {
      case "neutral":
        return neutral;
      case "primary":
        return primary;
      case "success":
        return success;
      case "warning":
        return warning;
      case "error":
        return error;
      case "info":
        return info;
      default:
        return getSemanticSurfaceColors(intent, context);
    }
  };

  const getInteraction = (
    intent: SemanticIntent,
  ): SemanticInteractionColors => {
    switch (intent) {
      case "neutral":
        return neutralInteraction;
      case "primary":
        return primaryInteraction;
      case "success":
        return successInteraction;
      case "warning":
        return warningInteraction;
      case "error":
        return errorInteraction;
      case "info":
        return infoInteraction;
      default:
        return getSemanticInteractionColors(intent);
    }
  };

  return {
    neutral,
    neutralInteraction,
    primary,
    primaryInteraction,
    success,
    successInteraction,
    warning,
    warningInteraction,
    error,
    errorInteraction,
    info,
    infoInteraction,
    getSurface,
    getInteraction,
  };
}
